/**
 * Open clinical tasks as Agent Home rows, plus a stash for Heidi launches
 * until the projects page has a Work project id.
 *
 * Do not import `@heidi/tasks`. Agents `/data` cannot take that door, so the
 * input type is the fields we actually read.
 */
import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns';
import { atom, getDefaultStore } from 'jotai';

import { formatRefTag, refTargetFrom } from '../../contracts/projects/ref-tags';
import type {
  WorkHomeAgeBucket,
  WorkHomeSuggestionAnalyticsFeatures,
} from '../../contracts/work-home-composer-analytics.types';

import { substituteStarterBundleItems } from './projects-starter-bundle-internal';

export type WorkHomeTaskBucket = 'overdue' | 'today' | 'week' | 'later' | 'nodate';

/** Same finder as tasks `getDelegatedWorkProjectId`. Copied so we stay off `@heidi/tasks`. */
const WORK_PROJECT_LINK_TYPE = 'WORK_PROJECT';

export interface WorkHomeTaskLink {
  type?: string | null;
  id?: string | null;
}

/** Fields we read. `ClinicalTask` from `@heidi/tasks` matches at every call site. */
export interface WorkHomeTaskSource {
  id: string;
  title?: string | null;
  status: string;
  due_date?: string | null;
  session_id?: string | null;
  session_name?: string | null;
  patient_name?: string | null;
  patient_profile_id?: string | null;
  suggested_prompt?: string | null;
  description?: string | null;
  links?: ReadonlyArray<WorkHomeTaskLink> | null;
  assignee_id?: string | null;
  assignee?: { id?: string | null } | null;
}

/** Shared empty list so `?? []` / default props do not rebuild the managed bundle. */
export const EMPTY_WORK_HOME_TASKS: readonly WorkHomeTaskSource[] = [];

/** What we need to hand one task to a Heidi Work chat. */
export interface WorkHomeTaskLaunch {
  taskId: string;
  title: string;
  description?: string;
  suggestedPrompt?: string;
  sessionId?: string;
  /** Work chat that already owns this task. Open that id. Don't create another. */
  workProjectId?: string;
  assigneeId?: string;
}

/** What a task row shows on its end, wherever that row is rendered. */
export interface WorkHomeTaskMeta {
  dueDate: Date | null;
  inProgress: boolean;
  /** The visit the task came from. Absent on tasks that stand alone. */
  source?: { sessionId: string; label: string };
}

/** Shared chip/row for Suggested work and the Work list. */
export interface WorkHomeWorkItem {
  id: string;
  label: string;
  /** Empty-composer stand-in while the row is hovered. Absent on leftover tasks. */
  hoverPreview?: string;
  /**
   * How `hoverPreview`'s `[slot]` seeds the composer on click. Unset strips
   * the brackets but keeps the words inside; `'mention'` stages
   * `mentionSeedLeadIn` followed by `@` instead, opening the @-mention
   * popout (a `[patient]`/`[visit]`/`[session]` slot); `'keep-brackets'`
   * leaves it exactly as shown (evidence/guideline-lookup rows). Never
   * affects the hover-preview badge itself.
   */
  hoverPreviewSeedMode?: 'mention' | 'keep-brackets';
  /** Required when `hoverPreviewSeedMode` is `'mention'`. See that field. */
  mentionSeedLeadIn?: string;
  /** Staged instead of `label` when the chip is not the instruction. */
  prompt?: string;
  /** Static prompt chips have no launch. They are just text. */
  launch?: WorkHomeTaskLaunch;
  /** Set on rows backed by a clinical task. Static prompt chips have none. */
  task?: WorkHomeTaskMeta;
  /** Ranking features stamped when the row is composed. */
  analytics?: WorkHomeSuggestionAnalyticsFeatures;
}

export interface WorkHomeTaskRow {
  id: string;
  title: string;
  bucket: WorkHomeTaskBucket;
  dueDate: Date | null;
  sessionId?: string;
  sessionLabel?: string;
  patientName?: string;
  patientProfileId?: string;
  /** Heidi can run it: `suggested_prompt` is set. */
  actionable: boolean;
  /** Status is `in_progress`. A clinician can flip that without handing the task to Heidi. */
  inProgress: boolean;
  launch: WorkHomeTaskLaunch;
}

/** One definition of what a task shows on its end, for every surface listing one. */
export const workHomeTaskMeta = (row: WorkHomeTaskRow): WorkHomeTaskMeta => {
  const label = row.sessionLabel ?? row.patientName;
  return {
    dueDate: row.dueDate,
    inProgress: row.inProgress,
    ...(row.sessionId && label ? { source: { sessionId: row.sessionId, label } } : {}),
  };
};

export const WORK_HOME_TASK_BUCKET_ORDER: Record<WorkHomeTaskBucket, number> = {
  overdue: 0,
  today: 1,
  week: 2,
  later: 3,
  nodate: 4,
};

/** Whole local calendar days between two dates, bucketed. */
export const workHomeAgeBucket = (from: Date, now: Date): WorkHomeAgeBucket => {
  const days = differenceInCalendarDays(now, from);
  if (days <= 0) return '0';
  if (days <= 2) return '1-2';
  if (days <= 6) return '3-6';
  if (days <= 13) return '7-13';
  if (days <= 29) return '14-29';
  return '30+';
};

/** `due_date` is `YYYY-MM-DD`. Parse local so "due today" doesn't jump a day. */
const parseTaskDueDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const bucketForDueDate = (dueDate: Date | null, now: Date): WorkHomeTaskBucket => {
  if (!dueDate) return 'nodate';
  const today = startOfDay(now);
  if (dueDate < today) return 'overdue';
  if (dueDate < addDays(today, 1)) return 'today';
  if (dueDate < addDays(today, 7)) return 'week';
  return 'later';
};

const workProjectIdFromLinks = (
  links?: ReadonlyArray<WorkHomeTaskLink> | null
): string | undefined => {
  const id = links?.find((link) => link.type === WORK_PROJECT_LINK_TYPE && link.id)?.id;
  return id ?? undefined;
};

const assigneeIdFromTask = (task: WorkHomeTaskSource): string | undefined =>
  task.assignee?.id ?? task.assignee_id ?? undefined;

const taskLaunchFromTask = (task: WorkHomeTaskSource): WorkHomeTaskLaunch => {
  const workProjectId = workProjectIdFromLinks(task.links);
  const assigneeId = assigneeIdFromTask(task);
  return {
    taskId: task.id,
    title: task.title?.trim() ?? '',
    ...(task.description?.trim() ? { description: task.description.trim() } : {}),
    ...(task.suggested_prompt?.trim()
      ? { suggestedPrompt: task.suggested_prompt.trim() }
      : {}),
    ...(task.session_id ? { sessionId: task.session_id } : {}),
    ...(workProjectId ? { workProjectId } : {}),
    ...(assigneeId ? { assigneeId } : {}),
  };
};

export const workProjectIdsById = (
  items: readonly { id: string; launch?: Pick<WorkHomeTaskLaunch, 'workProjectId'> }[]
): ReadonlyMap<string, string | undefined> =>
  new Map(items.map((item) => [item.id, item.launch?.workProjectId]));

/** Stay on one Work chat. Handed and unhanded don't mix.
 * Two handed tasks only stay together when they share `workProjectId`. */
export const nextCompatibleWorkItemIds = (
  currentIds: readonly string[],
  nextId: string,
  workProjectIdById: ReadonlyMap<string, string | undefined>
): readonly string[] => {
  if (currentIds.includes(nextId)) {
    return currentIds.filter((id) => id !== nextId);
  }
  const nextProjectId = workProjectIdById.get(nextId);
  return [
    ...currentIds.filter((id) => workProjectIdById.get(id) === nextProjectId),
    nextId,
  ];
};

export const buildWorkHomeTaskRows = (
  tasks: readonly WorkHomeTaskSource[],
  now: Date
): readonly WorkHomeTaskRow[] => {
  const rows = tasks.flatMap((task) => {
    const title = task.title?.trim();
    if (!title) return [];
    const dueDate = parseTaskDueDate(task.due_date);
    const launch = taskLaunchFromTask(task);
    return [
      {
        id: task.id,
        title,
        bucket: bucketForDueDate(dueDate, now),
        dueDate,
        ...(task.session_id ? { sessionId: task.session_id } : {}),
        ...(task.session_name?.trim() ? { sessionLabel: task.session_name.trim() } : {}),
        ...(task.patient_name?.trim() ? { patientName: task.patient_name.trim() } : {}),
        ...(task.patient_profile_id?.trim()
          ? { patientProfileId: task.patient_profile_id.trim() }
          : {}),
        actionable: launch.suggestedPrompt !== undefined,
        inProgress: task.status === 'in_progress',
        launch,
      },
    ];
  });
  // Agent Home exists to hand work to Heidi. Actionable first, then due date.
  return rows.sort((a, b) => {
    if (a.actionable !== b.actionable) return a.actionable ? -1 : 1;
    const byBucket =
      WORK_HOME_TASK_BUCKET_ORDER[a.bucket] - WORK_HOME_TASK_BUCKET_ORDER[b.bucket];
    if (byBucket !== 0) return byBucket;
    if (a.dueDate && b.dueDate && a.dueDate.getTime() !== b.dueDate.getTime()) {
      return a.dueDate.getTime() - b.dueDate.getTime();
    }
    return a.title.localeCompare(b.title);
  });
};

const sharedWorkProjectId = (
  launches: readonly WorkHomeTaskLaunch[]
): string | undefined => {
  const workProjectId = launches[0]?.workProjectId?.trim();
  if (!workProjectId) return undefined;
  return launches.every((launch) => launch.workProjectId?.trim() === workProjectId)
    ? workProjectId
    : undefined;
};

const launchTitles = (launches: readonly WorkHomeTaskLaunch[]): string =>
  launches
    .map((launch) => launch.title.trim())
    .filter(Boolean)
    .join(', ');

/** Open the Work chat that already owns these tasks when the composer is still
 * the managed starter — titles only, or `{leadIn} {titles}`. Typed extras
 * create a new chat instead of jumping back. */
export const existingWorkProjectIdForVerbatimLaunch = (
  launches: readonly WorkHomeTaskLaunch[],
  composerText: string,
  leadIn?: string
): string | undefined => {
  if (launches.length === 0) return undefined;
  const workProjectId = sharedWorkProjectId(launches);
  if (!workProjectId) return undefined;
  const titles = launchTitles(launches);
  if (!titles) return undefined;
  const trimmed = composerText.trim();
  if (!trimmed || trimmed === titles) return workProjectId;
  const prefix = leadIn?.trim();
  if (!prefix) return undefined;
  // Same substitution the composer serializer uses, so the "still the managed
  // starter" comparison cannot drift from what the composer rendered.
  return trimmed === substituteStarterBundleItems(prefix, titles)
    ? workProjectId
    : undefined;
};

/** Agent-only line. `parseUserPreamble` strips it the same way as the
 * selected-skill instruction so it never reaches the clinician bubble. */
export const TASK_LAUNCH_INSTRUCTION =
  'Action each task using the linked context where available. When a task is fully completed, mark it done with the update_task_status tool; otherwise leave it in progress.';

const taskRefTag = ({ taskId, title }: WorkHomeTaskLaunch): string | null => {
  const target = refTargetFrom('task', taskId);
  return target && title.trim() ? formatRefTag({ ...target, labelHint: title }) : null;
};

/** Same `@<type>: <label> [id=…]` grammar as `serializeLinkedContext`, or a task ref per line. */
export const buildTaskLaunchPromptPreamble = (
  launches: readonly WorkHomeTaskLaunch[],
  { refs = false }: { refs?: boolean } = {}
): string => {
  if (launches.length === 0) return '';
  const lines = launches.map(
    (launch) =>
      (refs && taskRefTag(launch)) || `@task: ${launch.title} [id=${launch.taskId}]`
  );
  return `${TASK_LAUNCH_INSTRUCTION}\n\n${lines.join('\n')}\n\n`;
};

const fallbackTaskInstruction = (launch: WorkHomeTaskLaunch): string => {
  const description = launch.description?.trim();
  return [
    'Complete this task using Work and the linked session context.',
    `Title: ${launch.title}`,
    `Description: ${description || '(none)'}`,
  ].join('\n');
};

/** Clinician-visible first-turn body. Task identity lives on the preamble. */
export const buildTaskLaunchPrompt = (
  launches: readonly WorkHomeTaskLaunch[]
): string => {
  if (launches.length === 0) return '';
  return launches
    .map((launch) => launch.suggestedPrompt?.trim() || fallbackTaskInstruction(launch))
    .join('\n\n');
};

const pendingWorkTaskLaunchesAtom = atom<readonly WorkHomeTaskLaunch[]>([]);

export const stashPendingWorkTaskLaunches = (
  launches: readonly WorkHomeTaskLaunch[]
): void => {
  getDefaultStore().set(pendingWorkTaskLaunchesAtom, launches);
};

/** One create owns one launch set. Read, then clear. */
export const takePendingWorkTaskLaunches = (): readonly WorkHomeTaskLaunch[] => {
  const store = getDefaultStore();
  const launches = store.get(pendingWorkTaskLaunchesAtom);
  if (launches.length > 0) store.set(pendingWorkTaskLaunchesAtom, []);
  return launches;
};

export const clearPendingWorkTaskLaunches = (): void => {
  getDefaultStore().set(pendingWorkTaskLaunchesAtom, []);
};
